import { dayLabel } from '../design/dates';
import type { Arc } from '../plan/arc';
import { heldArcsOf, startWithOf, wayIn } from './workspace';
import type { Workspace } from './workspace';

/**
 * What a nook's card shows under its head — the app's `NookCardFace`, read off
 * the nook's own file.
 */

/** How many of its newest sources a card reads: one to lead when nothing else does, and the lines under it. */
export const LATEST_SOURCES = 3;

/** The fewest placed things that make a strip: one alone is not a layout. */
export const STRIP_MIN_PLACED = 2;

/** Lines the one secondary kind takes under the explicit parts. */
const SECONDARY_LINES = 2;

/** Lines it takes when a Face, a Next and a strip already stand above it. */
const SECONDARY_LINES_CROWDED = 1;

/** The same on a card opened out. */
const OPEN_LINES = 5;
const OPEN_LINES_CROWDED = 3;

/** One thing a nook holds, as its card names it: a recording once, however many of its topics it holds. */
export interface HeldSource {
  path: string;
  /** What its member line calls it. */
  label: string;
}

/** The one kind of thing under the explicit parts — the first of these the nook has. */
export type Secondary =
  | { kind: 'none' }
  /** Waiting on the person first, then the rest in the nook's order. */
  | { kind: 'arcs'; arcs: Arc[] }
  /** Its talk. The file says only that there is one, not what was said in it. */
  | { kind: 'talk' }
  /** What it holds, newest first, a line each. */
  | { kind: 'newest'; sources: HeldSource[] };

export interface CardFace {
  /** Its Face leads, every pick of it. */
  picks: boolean;
  /** Its Next: leading in the face's serif, or as a labelled line under a Face. */
  next: { text: string; leads: boolean } | null;
  /** With nothing explicit, the newest thing it holds leads in the Next's place. */
  lead: HeldSource | null;
  /** Its canvas, as a strip. */
  strip: boolean;
  secondary: Secondary;
  /** The card is its canvas alone, edge to edge. */
  fullBleed: boolean;
}

const NONE: Secondary = { kind: 'none' };

/**
 * The card's composition. The explicit parts lead and never give way: the
 * Face, then the Next — its own, else the way-in arc's — then the strip once
 * [STRIP_MIN_PLACED] things are placed. Under them ONE kind: the open arcs,
 * else the talk, else the newest things it holds; an arc the Next names is
 * skipped, as said once already. A nook with no name, no Face and no Next is
 * its canvas. [arcsByName] is null while the plan is being read: until then a
 * nook that holds arcs says nothing under its explicit parts, rather than
 * something the arcs would replace.
 */
export function cardFace(w: Workspace, named: boolean, arcsByName: ReadonlyMap<string, Arc> | null, open = false): CardFace {
  const held = arcsByName ? heldArcsOf(w, arcsByName) : null;
  const next = startWithOf(w.startWith, held);
  const hasPicks = w.face.length > 0;
  if (!named && !hasPicks && next === null) {
    return { picks: false, next: null, lead: null, strip: false, secondary: NONE, fullBleed: true };
  }
  const nextArc = !w.startWith && next !== null ? wayIn(w, arcsByName) : null;
  const strip = w.plane.length >= STRIP_MIN_PLACED;
  const crowded = hasPicks && next !== null && strip;
  const lines = open ? (crowded ? OPEN_LINES_CROWDED : OPEN_LINES) : crowded ? SECONDARY_LINES_CROWDED : SECONDARY_LINES;
  const arcs = (held ?? [])
    .filter((arc) => arc.status !== 'done' && arc.status !== 'parked')
    .filter((arc) => next === null || !names(next, nextArc, arc))
    .sort((a, b) => (a.status === 'needs-input' ? 0 : 1) - (b.status === 'needs-input' ? 0 : 1));
  const known = held !== null || w.arcs.length === 0;
  const talk = w.talk !== null;
  const latest = latestSources(w, open ? OPEN_LINES + 1 : LATEST_SOURCES);
  const explicit = hasPicks || next !== null || strip;
  const lead = known && !explicit && arcs.length === 0 && !talk ? (latest[0] ?? null) : null;
  const newest = latest.slice(lead ? 1 : 0, (lead ? 1 : 0) + lines);
  const secondary: Secondary = !known
    ? NONE
    : arcs.length > 0
      ? { kind: 'arcs', arcs: arcs.slice(0, lines) }
      : talk
        ? { kind: 'talk' }
        : newest.length > 0
          ? { kind: 'newest', sources: newest }
          : NONE;
  return {
    picks: hasPicks,
    next: next === null ? null : { text: next, leads: !hasPicks },
    lead,
    strip,
    secondary,
    fullBleed: false,
  };
}

/** Whether [next] already names [arc]: it is that arc's own first step, or it says the arc's name. */
function names(next: string, nextArc: Arc | null, arc: Arc): boolean {
  const title = arc.title.trim();
  return arc === nextArc || (title !== '' && next.toLowerCase().includes(title.toLowerCase()));
}

const STAMP = /(\d{4})-(\d{2})-(\d{2}) at (\d{2})-(\d{2})/;

/**
 * What it holds and has kept, newest first, each as its file: a topic stands
 * for the recording it belongs to, and a recording is listed once. Gathered
 * sources still waiting to be kept stay off the card. The file keeps no time a
 * source joined, so newest is when it was recorded, as its path says; one with
 * no time in its path follows, in the file's order.
 */
export function latestSources(w: Workspace, limit = LATEST_SOURCES): HeldSource[] {
  const unconfirmed = new Set(w.unconfirmed);
  const ranked = w.sources
    .filter((ref) => !unconfirmed.has(ref.anchor ? `${ref.path}#^${ref.anchor}` : ref.path))
    .map((ref, i) => ({ ref, i, at: Number(ref.path.match(STAMP)?.slice(1).join('') ?? -1) }))
    .sort((a, b) => b.at - a.at || a.i - b.i);
  const out: HeldSource[] = [];
  const seen = new Set<string>();
  for (const { ref } of ranked) {
    if (out.length >= limit) break;
    if (seen.has(ref.path)) continue;
    seen.add(ref.path);
    out.push({ path: ref.path, label: ref.label });
  }
  return out;
}

const UNTITLED = /^(?:Recording|Note|Text note)? ?\d{4}-\d{2}-\d{2} at \d{2}-\d{2}$/;

/**
 * One held thing as a line on the card: the name someone gave it — the
 * catalog's first, as the fresher record of a rename — else when it was
 * recorded, `Tue 14 oct · 10:48`. Null when neither is known.
 */
export function sourceWords(source: HeldSource, catalogName: string | null): string | null {
  const given = [catalogName, source.label].find((name) => name && name.trim() && !UNTITLED.test(name.trim()));
  if (given) return given.trim();
  const m = source.path.match(STAMP);
  if (!m) return source.label.trim() || null;
  const [, y, mo, d, hh, mm] = m;
  const day = dayLabel(new Date(Number(y), Number(mo) - 1, Number(d)));
  const words = `${day} · ${hh}:${mm}`;
  return words.charAt(0).toUpperCase() + words.slice(1);
}
